import { logger } from '@/services/logging';
import * as Tone from 'tone';
import { BackgroundMusicPlayer } from './backgroundMusic';
import type { MusicThemeId } from './backgroundMusic';
import type { WorldId } from '@/config/worlds';

export class ToneSoundManager {
  private enabled = true;
  private volume = 0.3;
  private vibrationEnabled = true;
  private initialized = false;
  private synths: Record<string, Tone.PolySynth | Tone.Synth> = {};
  private masterVolume?: Tone.Volume;
  private reverb?: Tone.Reverb;
  private delay?: Tone.FeedbackDelay;
  private filter?: Tone.Filter;
  private chorus?: Tone.Chorus;
  private backgroundMusic: BackgroundMusicPlayer;

  // Track pending scheduled events so we can cancel them on disable/dispose
  private pendingTimeouts: ReturnType<typeof setTimeout>[] = [];
  private pendingTransportIds: number[] = [];

  // Sound throttling to prevent voice accumulation
  private lastPlayTime = new Map<string, number>();
  private readonly minIntervals: Record<string, number> = {
    playEnemyMove: 200,
    playCollectCoin: 100,
    playMove: 50,
    playWallHit: 100,
    playIceSlide: 150,
    playModalOpen: 150,
    playModalClose: 150,
  };

  // Musical scales for melodic sounds -- all C major compatible
  private scales = {
    major: ['C3', 'E3', 'G3', 'C4', 'E4', 'G4'],
    pentatonic: ['C3', 'D3', 'E3', 'G3', 'A3', 'C4'],
    ambient: ['C2', 'G2', 'C3', 'E3', 'G3', 'B3', 'D4', 'G4'],
  };
  private currentScale = this.scales.ambient;

  constructor() {
    // Load settings
    this.enabled = localStorage.getItem('soundEnabled') !== 'false';
    this.volume = parseFloat(localStorage.getItem('soundVolume') || '0.3');
    this.vibrationEnabled = localStorage.getItem('vibrationEnabled') !== 'false';
    this.backgroundMusic = new BackgroundMusicPlayer();

    logger.info('sound', 'ToneSoundManager created', {
      enabled: this.enabled,
      volume: this.volume,
      vibrationEnabled: this.vibrationEnabled
    });
  }

  async initialize(): Promise<void> {
    if (this.initialized) return;

    try {
      // Start Tone.js audio context
      await Tone.start();

      // Create master volume control with lower base volume for softer sounds
      this.masterVolume = new Tone.Volume(Tone.gainToDb(this.volume * 0.7)).toDestination();
      
      // Create ambient effects chain
      this.reverb = new Tone.Reverb({ decay: 4, wet: 0.3 }).connect(this.masterVolume);
      this.delay = new Tone.FeedbackDelay({ delayTime: '8n', feedback: 0.2, wet: 0.2 }).connect(this.reverb!);
      this.filter = new Tone.Filter({ frequency: 4000, type: 'lowpass' }).connect(this.delay);
      this.chorus = new Tone.Chorus({ frequency: 0.5, depth: 0.3, wet: 0.3 }).connect(this.filter);

      // Create different synthesizers for various sound effects
      this.synths = {
        // UI sounds - softer, more mellow
        button: new Tone.PolySynth(Tone.Synth, {
          oscillator: { type: 'sine' },
          envelope: {
            attack: 0.02,
            decay: 0.1,
            sustain: 0.2,
            release: 0.3,
          },
          volume: -12,
        }).connect(this.chorus),

        // Movement sounds - gentle water drops
        movement: new Tone.PolySynth(Tone.Synth, {
          oscillator: { type: 'sine' },
          envelope: {
            attack: 0.02,
            decay: 0.15,
            sustain: 0.05,
            release: 0.5,
          },
          volume: -15,
        }).connect(this.reverb),

        // Success/achievement sounds - warm bells
        success: new Tone.PolySynth(Tone.Synth, {
          oscillator: { type: 'triangle' },
          envelope: {
            attack: 0.01,
            decay: 0.3,
            sustain: 0.4,
            release: 1.5,
          },
          volume: -10,
        }).connect(this.reverb),

        // Error/collision sounds - soft wooden percussion
        error: new Tone.PolySynth(Tone.Synth, {
          oscillator: { type: 'sine' },
          envelope: {
            attack: 0.001,
            decay: 0.05,
            sustain: 0.0,
            release: 0.2,
          },
          volume: -8,
        }).connect(this.filter),

        // Ambient/background - ethereal pad
        ambient: new Tone.PolySynth(Tone.Synth, {
          oscillator: { type: 'sine' },
          envelope: {
            attack: 3,
            decay: 2,
            sustain: 0.7,
            release: 5,
          },
          volume: -20,
        }).connect(this.reverb),
      };

      // Cap voice count on ALL synths to prevent unbounded voice accumulation
      (this.synths.button as Tone.PolySynth).maxPolyphony = 4;
      (this.synths.movement as Tone.PolySynth).maxPolyphony = 8;
      (this.synths.success as Tone.PolySynth).maxPolyphony = 6;
      (this.synths.error as Tone.PolySynth).maxPolyphony = 8;
      (this.synths.ambient as Tone.PolySynth).maxPolyphony = 4;

      // Initialize background music player with the shared master volume.
      // Isolated try/catch so music failure doesn't kill SFX.
      try {
        this.backgroundMusic.initialize(this.masterVolume);
      } catch (musicError) {
        logger.error('sound', 'Background music initialization failed (SFX still active)', musicError);
      }

      this.initialized = true;
      logger.info('sound', 'Tone.js initialized successfully', {
        synthsCreated: Object.keys(this.synths)
      });
    } catch (error) {
      logger.error('sound', 'Tone.js initialization failed', error);
      this.enabled = false;
    }
  }

  async resume(): Promise<void> {
    if (Tone.context.state === 'suspended') {
      await Tone.context.resume();
    }
  }

  private vibrate(pattern: number | number[]): void {
    if (this.vibrationEnabled && window.navigator && window.navigator.vibrate) {
      window.navigator.vibrate(pattern);
    }
  }

  /**
   * Schedule a delayed callback using the audio clock (Tone.Transport) when
   * available so that timing is independent of main-thread jank.  Falls back
   * to setTimeout when the Transport is not running.
   */
  private scheduleTimeout(fn: () => void, ms: number): void {
    // Use Tone.Transport for audio-clock scheduling when the context is running
    if (this.initialized && Tone.getContext().state === 'running') {
      try {
        const seconds = ms / 1000;
        const eventId = Tone.getTransport().scheduleOnce((time) => {
          // Remove from tracking
          const idx = this.pendingTransportIds.indexOf(eventId);
          if (idx !== -1) this.pendingTransportIds.splice(idx, 1);
          if (this.enabled && this.initialized) {
            try { fn(); } catch { /* swallow - synth may have been disposed */ }
          }
        }, `+${seconds}`);
        this.pendingTransportIds.push(eventId);

        // Ensure transport is running
        if (Tone.getTransport().state !== 'started') {
          Tone.getTransport().start();
        }
        return;
      } catch {
        // Fall through to setTimeout if Transport fails
      }
    }

    // Fallback: regular setTimeout
    const id = setTimeout(() => {
      const idx = this.pendingTimeouts.indexOf(id);
      if (idx !== -1) this.pendingTimeouts.splice(idx, 1);
      if (this.enabled && this.initialized) {
        try { fn(); } catch { /* swallow - synth may have been disposed */ }
      }
    }, ms);
    this.pendingTimeouts.push(id);
  }

  /**
   * Cancel all pending sound timeouts and Transport events.
   */
  private clearPendingTimeouts(): void {
    for (const id of this.pendingTimeouts) clearTimeout(id);
    this.pendingTimeouts.length = 0;
    for (const id of this.pendingTransportIds) {
      try { Tone.getTransport().clear(id); } catch { /* ignore */ }
    }
    this.pendingTransportIds.length = 0;
  }

  /**
   * Release all active voices on every PolySynth to free resources.
   */
  private releaseAllVoices(): void {
    for (const synth of Object.values(this.synths)) {
      try {
        if (synth instanceof Tone.PolySynth) synth.releaseAll();
      } catch { /* ignore */ }
    }
  }

  /**
   * Throttle guard: returns false if the sound was played too recently.
   */
  private canPlay(soundName: string): boolean {
    const minInterval = this.minIntervals[soundName];
    if (!minInterval) return true;
    const now = performance.now();
    const last = this.lastPlayTime.get(soundName) || 0;
    if (now - last < minInterval) return false;
    this.lastPlayTime.set(soundName, now);
    return true;
  }

  // Settings management
  setVolume(volume: number): void {
    this.volume = Math.max(0, Math.min(1, volume));
    localStorage.setItem('soundVolume', this.volume.toString());
    if (this.masterVolume) {
      this.masterVolume.volume.value = Tone.gainToDb(this.volume * 0.7);
    }
    logger.info('sound', `Volume set to ${this.volume}`);
  }

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
    localStorage.setItem('soundEnabled', this.enabled.toString());
    if (!this.enabled) {
      // Cancel pending sound chains, release active voices, mute
      this.clearPendingTimeouts();
      this.releaseAllVoices();
      if (this.masterVolume) {
        this.masterVolume.volume.value = -Infinity;
      }
    } else if (this.masterVolume) {
      this.masterVolume.volume.value = Tone.gainToDb(this.volume * 0.7);
    }
    logger.info('sound', `Sound ${enabled ? 'enabled' : 'disabled'}`);
  }

  toggleVibration(): void {
    this.vibrationEnabled = !this.vibrationEnabled;
    localStorage.setItem('vibrationEnabled', this.vibrationEnabled.toString());
    logger.info('sound', `Vibration ${this.vibrationEnabled ? 'enabled' : 'disabled'}`);
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  getVolume(): number {
    return this.volume;
  }
  
  getBackgroundMusic(): BackgroundMusicPlayer {
    return this.backgroundMusic;
  }

  // UI Sound Effects
  async playButtonClick(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    const synth = this.synths.button as Tone.PolySynth;
    synth.triggerAttackRelease(['G3', 'C4'], '16n', undefined, 0.3);
    this.vibrate(5);
    logger.debug('sound', 'Playing button click');
  }

  async playButtonHover(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    this.synths.button.triggerAttackRelease('E4', '32n', undefined, 0.2);
    logger.debug('sound', 'Playing button hover');
  }

  // Movement Sound Effects
  async playMove(): Promise<void> {
    if (!this.enabled || !this.canPlay('playMove')) return;
    await this.initialize();
    await this.resume();
    const note = this.currentScale[Math.floor(Math.random() * this.currentScale.length)];
    this.synths.movement.triggerAttackRelease(note, '8n', undefined, 0.3);
    this.vibrate(3);
    logger.debug('sound', 'Playing move');
  }

  async playSlide(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Create a gentle sliding sound with pitch bend
    const startNote = this.currentScale[Math.floor(Math.random() * 3)];
    const endNote = this.currentScale[Math.floor(Math.random() * 3) + 3];
    const synth = this.synths.movement as Tone.PolySynth;
    synth.triggerAttackRelease([startNote, endNote], '4n', undefined, 0.2);
    this.vibrate([3, 5, 3]);
    logger.debug('sound', 'Playing slide');
  }

  // Game Event Sound Effects
  async playLevelStart(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Gentle ascending notes to indicate level start - like wind chimes
    const notes = ['C3', 'E3', 'G3', 'C4'];
    let delay = 0;
    notes.forEach((note, i) => {
      this.synths.success.triggerAttackRelease(note, '2n', `+${delay}`, 0.2 - i * 0.03);
      delay += 0.15;
    });
    this.vibrate(10);
    logger.debug('sound', 'Playing level start');
  }

  async playLevelComplete(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Magical completion sound - like distant bells
    const chord = ['C3', 'E3', 'G3', 'B3', 'E4', 'G4'];
    const synth = this.synths.success as Tone.PolySynth;
    
    // Staggered bell-like sounds
    let delay = 0;
    chord.forEach((note, i) => {
      synth.triggerAttackRelease(note, '2n', `+${delay}`, 0.3 - i * 0.04);
      delay += 0.08;
    });
    
    // Add a subtle high shimmer
    this.scheduleTimeout(() => {
      synth.triggerAttackRelease(['C5', 'E5'], '4n', undefined, 0.1);
    }, 400);
    
    this.vibrate([30, 50, 30, 50, 100]);
    logger.debug('sound', 'Playing level complete');
  }

  async playGameOver(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Somber, gentle failure sound - like distant fog horn
    const notes = ['F2', 'E2', 'D2'];
    let delay = 0;
    notes.forEach(note => {
      this.synths.ambient.triggerAttackRelease(note, '2n', `+${delay}`, 0.3);
      delay += 0.3;
    });
    this.vibrate([100, 50, 100]);
    logger.debug('sound', 'Playing game over');
  }

  async playLevelFailed(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Gentle disappointment sound - like a sigh
    const synth = this.synths.ambient as Tone.PolySynth;
    synth.triggerAttackRelease(['G2', 'C3'], '1n', undefined, 0.3);
    this.vibrate([50, 30, 50]);
    logger.debug('sound', 'Playing level failed');
  }

  async playHitObstacle(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Soft thud for obstacle hit
    const synth = this.synths.error as Tone.PolySynth;
    synth.triggerAttackRelease(['F2', 'G2'], '16n', undefined, 0.4);
    this.vibrate(50);
    logger.debug('sound', 'Playing hit obstacle');
  }

  async playWallHit(): Promise<void> {
    if (!this.enabled || !this.canPlay('playWallHit')) return;
    await this.initialize();
    await this.resume();
    // Short percussive thud - stone impact
    const synth = this.synths.error as Tone.PolySynth;
    synth.triggerAttackRelease(['D2', 'G2'], '32n', undefined, 0.3);
    this.vibrate(25);
    logger.debug('sound', 'Playing wall hit');
  }

  async playIceSlide(): Promise<void> {
    if (!this.enabled || !this.canPlay('playIceSlide')) return;
    await this.initialize();
    await this.resume();
    // Crystalline slide - high shimmer
    const synth = this.synths.movement as Tone.PolySynth;
    synth.triggerAttackRelease(['E5', 'B5'], '16n', undefined, 0.12);
    logger.debug('sound', 'Playing ice slide');
  }

  async playFireHit(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Crackling flame impact
    const errorSynth = this.synths.error as Tone.PolySynth;
    errorSynth.triggerAttackRelease(['C2', 'E2'], '8n', undefined, 0.35);
    this.scheduleTimeout(() => {
      this.synths.ambient.triggerAttackRelease('A1', '4n', undefined, 0.15);
    }, 60);
    this.vibrate([30, 50, 30]);
    logger.debug('sound', 'Playing fire hit');
  }

  async playFreeze(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Crystalline freeze burst - descending shimmer
    const synth = this.synths.success as Tone.PolySynth;
    const notes = ['B5', 'G5', 'E5', 'C5', 'G4'];
    let delay = 0;
    notes.forEach((note, i) => {
      synth.triggerAttackRelease(note, '8n', `+${delay}`, 0.2 - i * 0.02);
      delay += 0.04;
    });
    this.vibrate([10, 20, 10]);
    logger.debug('sound', 'Playing freeze');
  }

  async playWarp(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Dimensional rift - dissonant then resolved
    const synth = this.synths.movement as Tone.PolySynth;
    synth.triggerAttackRelease(['C3', 'G3'], '16n', undefined, 0.25);
    this.scheduleTimeout(() => {
      synth.triggerAttackRelease(['G4', 'D5'], '8n', undefined, 0.2);
    }, 80);
    this.vibrate([20, 40, 20]);
    logger.debug('sound', 'Playing warp');
  }

  async playCollectCoin(): Promise<void> {
    if (!this.enabled || !this.canPlay('playCollectCoin')) return;
    await this.initialize();
    await this.resume();
    // Sparkly coin sound - like tiny bells
    const notes = ['G4', 'B4', 'D5', 'G5'];
    let delay = 0;
    notes.forEach((note, i) => {
      this.synths.success.triggerAttackRelease(note, '16n', `+${delay}`, 0.15 - i * 0.02);
      delay += 0.02;
    });
    this.vibrate(10);
    logger.debug('sound', 'Playing collect coin');
  }

  async playPowerup(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Mystical powerup sound - like crystal resonance
    const notes = ['C3', 'G3', 'E4', 'B4', 'G5'];
    let delay = 0;
    notes.forEach((note, i) => {
      this.synths.ambient.triggerAttackRelease(note, '2n', `+${delay}`, 0.2 - i * 0.03);
      delay += 0.04;
    });
    this.vibrate([15, 30, 15]);
    logger.debug('sound', 'Playing powerup');
  }

  // Ability-specific sounds

  async playShield(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Metallic clang with resonant hum - like a force field activating
    const synth = this.synths.success as Tone.PolySynth;
    synth.triggerAttackRelease(['C3', 'G3'], '4n', undefined, 0.25);
    this.scheduleTimeout(() => {
      synth.triggerAttackRelease(['E4', 'G4'], '2n', undefined, 0.15);
    }, 100);
    this.vibrate([20, 40, 20]);
    logger.debug('sound', 'Playing shield');
  }

  async playInvincibility(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Ascending power chord - feels heroic
    const notes = ['C3', 'E3', 'G3', 'C4', 'E4', 'G4', 'C5'];
    let delay = 0;
    notes.forEach((note, i) => {
      this.synths.success.triggerAttackRelease(note, '4n', `+${delay}`, 0.25 - i * 0.02);
      delay += 0.06;
    });
    this.vibrate([10, 20, 10, 20, 40]);
    logger.debug('sound', 'Playing invincibility');
  }

  async playTeleport(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Whoosh + warble - sucked in then expelled
    const synth = this.synths.movement as Tone.PolySynth;
    // Descending (suck in)
    synth.triggerAttackRelease('G4', '16n', undefined, 0.3);
    this.scheduleTimeout(() => synth.triggerAttackRelease('D4', '16n', undefined, 0.25), 50);
    this.scheduleTimeout(() => synth.triggerAttackRelease('G3', '16n', undefined, 0.2), 100);
    // Ascending (grow out)
    this.scheduleTimeout(() => {
      synth.triggerAttackRelease('G3', '16n', undefined, 0.2);
      this.scheduleTimeout(() => synth.triggerAttackRelease('D4', '16n', undefined, 0.25), 50);
      this.scheduleTimeout(() => synth.triggerAttackRelease('G4', '16n', undefined, 0.3), 100);
    }, 250);
    this.vibrate([30, 50, 30]);
    logger.debug('sound', 'Playing teleport');
  }

  async playDoubleCoins(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Double sparkle - two rapid coin-like chimes
    const synth = this.synths.success as Tone.PolySynth;
    synth.triggerAttackRelease(['G4', 'B4'], '16n', undefined, 0.2);
    this.scheduleTimeout(() => {
      synth.triggerAttackRelease(['G4', 'B4', 'D5'], '8n', undefined, 0.25);
    }, 120);
    this.vibrate([10, 15, 10]);
    logger.debug('sound', 'Playing doubleCoins');
  }

  async playMagnetCoins(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Magnetic hum with rising overtones
    const successSynth = this.synths.success as Tone.PolySynth;
    this.synths.ambient.triggerAttackRelease('E2', '2n', undefined, 0.15);
    this.scheduleTimeout(() => {
      successSynth.triggerAttackRelease(['E3', 'B3'], '4n', undefined, 0.12);
    }, 200);
    this.vibrate([5, 10, 5, 10, 5]);
    logger.debug('sound', 'Playing magnetCoins');
  }

  async playExtraLife(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Warm, reassuring pulse - heartbeat-like
    const synth = this.synths.success as Tone.PolySynth;
    synth.triggerAttackRelease(['C3', 'E3', 'G3'], '4n', undefined, 0.2);
    this.scheduleTimeout(() => {
      synth.triggerAttackRelease(['C4', 'E4', 'G4'], '2n', undefined, 0.25);
    }, 200);
    this.vibrate([40, 80, 40]);
    logger.debug('sound', 'Playing extraLife');
  }

  async playSkipLevel(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Quick whoosh upward - like fast-forwarding
    const notes = ['C3', 'E3', 'G3', 'C4', 'E4', 'C5'];
    let delay = 0;
    notes.forEach((note, i) => {
      this.synths.movement.triggerAttackRelease(note, '32n', `+${delay}`, 0.2);
      delay += 0.03;
    });
    this.vibrate([5, 10, 5, 10, 20]);
    logger.debug('sound', 'Playing skipLevel');
  }

  async playHint(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Gentle revelation - like a lightbulb moment
    const synth = this.synths.success as Tone.PolySynth;
    synth.triggerAttackRelease('E4', '8n', undefined, 0.15);
    this.scheduleTimeout(() => {
      synth.triggerAttackRelease(['G4', 'B4'], '4n', undefined, 0.2);
    }, 150);
    this.vibrate(10);
    logger.debug('sound', 'Playing hint');
  }

  async playUndo(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Reverse sound - descending quick notes
    const notes = ['G4', 'E4', 'C4', 'G3'];
    let delay = 0;
    notes.forEach(note => {
      this.synths.movement.triggerAttackRelease(note, '16n', `+${delay}`, 0.2);
      delay += 0.05;
    });
    this.vibrate([10, 20]);
    logger.debug('sound', 'Playing undo');
  }

  async playSlowTime(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Deep, slow resonance - like time stretching
    this.synths.ambient.triggerAttackRelease('C2', '1n', undefined, 0.2);
    this.scheduleTimeout(() => {
      this.synths.ambient.triggerAttackRelease('G2', '1n', undefined, 0.15);
    }, 300);
    this.vibrate([20, 40, 20, 40]);
    logger.debug('sound', 'Playing slowTime');
  }

  async playAchievement(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Ethereal achievement sound - like discovering a secret
    const melody = ['C3', 'G3', 'C4', 'E4', 'G4', 'C5'];
    const harmony = ['E2', 'B2', 'E3', 'G3', 'B3', 'E4'];
    
    let delay = 0;
    melody.forEach((note, index) => {
      this.synths.success.triggerAttackRelease(note, '2n', `+${delay}`, 0.25 - index * 0.03);
      if (index < harmony.length) {
        this.synths.ambient.triggerAttackRelease(harmony[index], '4n', `+${delay}`, 0.15);
      }
      delay += 0.12;
    });
    this.vibrate([50, 30, 50, 30, 100]);
    logger.debug('sound', 'Playing achievement');
  }

  // Enemy sounds
  async playEnemyMove(): Promise<void> {
    if (!this.enabled || !this.canPlay('playEnemyMove')) return;
    await this.initialize();
    await this.resume();
    const synth = this.synths.error as Tone.PolySynth;
    synth.triggerAttackRelease('D2', '32n', undefined, 0.15);
    this.vibrate(3);
    logger.debug('sound', 'Playing enemy move');
  }

  async playHurt(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    // Quick descending interval -- sympathetic "ow"
    const synth = this.synths.error as Tone.PolySynth;
    synth.triggerAttackRelease('E3', '16n', undefined, 0.3);
    this.scheduleTimeout(() => {
      synth.triggerAttackRelease('C3', '16n', undefined, 0.25);
    }, 60);
    this.vibrate([40, 20, 40]);
    logger.debug('sound', 'Playing hurt');
  }

  async playEnemyCollision(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    const synth = this.synths.error as Tone.PolySynth;
    synth.triggerAttackRelease(['C2', 'G2'], '8n', undefined, 0.4);
    this.vibrate([50, 30, 50]);
    logger.debug('sound', 'Playing enemy collision');
  }

  async playShieldAbsorb(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    const successSynth = this.synths.success as Tone.PolySynth;
    const errorSynth = this.synths.error as Tone.PolySynth;
    successSynth.triggerAttackRelease(['E4', 'G4'], '16n', undefined, 0.25);
    this.scheduleTimeout(() => {
      errorSynth.triggerAttackRelease('C2', '16n', undefined, 0.15);
    }, 80);
    this.vibrate([20, 30, 20]);
    logger.debug('sound', 'Playing shield absorb');
  }

  // Card sounds
  async playCardDraw(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    const synth = this.synths.button as Tone.PolySynth;
    synth.triggerAttackRelease(['C4', 'E4'], '8n', undefined, 0.25);
    this.vibrate(10);
    logger.debug('sound', 'Playing card draw');
  }

  async playCardPlay(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    const synth = this.synths.movement as Tone.PolySynth;
    const notes = ['C4', 'E4', 'G4'];
    let delay = 0;
    notes.forEach(note => {
      synth.triggerAttackRelease(note, '16n', `+${delay}`, 0.25);
      delay += 0.06;
    });
    this.vibrate([10, 15, 10]);
    logger.debug('sound', 'Playing card play');
  }

  // Modal sounds
  async playModalOpen(): Promise<void> {
    if (!this.enabled || !this.canPlay('playModalOpen')) return;
    await this.initialize();
    await this.resume();
    this.synths.button.triggerAttackRelease('A3', '32n', undefined, 0.2);
    logger.debug('sound', 'Playing modal open');
  }

  async playModalClose(): Promise<void> {
    if (!this.enabled || !this.canPlay('playModalClose')) return;
    await this.initialize();
    await this.resume();
    this.synths.button.triggerAttackRelease('E3', '32n', undefined, 0.2);
    logger.debug('sound', 'Playing modal close');
  }

  // Level transition
  async playLevelTransition(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    const synth = this.synths.success as Tone.PolySynth;
    const notes = ['C4', 'E4', 'G4'];
    let delay = 0;
    notes.forEach((note, i) => {
      synth.triggerAttackRelease(note, '4n', `+${delay}`, 0.2 - i * 0.03);
      delay += 0.1;
    });
    this.vibrate(15);
    logger.debug('sound', 'Playing level transition');
  }

  // ─── Background Music ───────────────────────────────────────────

  async startWorldMusic(worldId: WorldId): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    await this.backgroundMusic.startMusic(worldId);
  }

  async startLobbyMusic(): Promise<void> {
    if (!this.enabled) return;
    await this.initialize();
    await this.resume();
    await this.backgroundMusic.startMusic('lobby');
  }

  stopWorldMusic(): void {
    this.backgroundMusic.stopMusic();
  }

  setMusicEnabled(enabled: boolean): void {
    this.backgroundMusic.setEnabled(enabled);
  }

  isMusicEnabled(): boolean {
    return this.backgroundMusic.isEnabled();
  }

  setMusicVolume(vol: number): void {
    this.backgroundMusic.setVolume(vol);
  }

  getMusicVolume(): number {
    return this.backgroundMusic.getVolume();
  }

  // Clean up resources
  dispose(): void {
    this.clearPendingTimeouts();
    this.releaseAllVoices();
    this.backgroundMusic.dispose();
    Object.values(this.synths).forEach(synth => synth.dispose());
    this.synths = {};
    if (this.reverb) this.reverb.dispose();
    if (this.delay) this.delay.dispose();
    if (this.filter) this.filter.dispose();
    if (this.chorus) this.chorus.dispose();
    if (this.masterVolume) this.masterVolume.dispose();
    this.initialized = false;
    logger.info('sound', 'ToneSoundManager disposed');
  }
}
